import React, { useState } from 'react';
import './PayoutModal.css';

/**
 * Beautiful Payout Modal Component
 * Shows confirmation dialog with detailed breakdown and processing status
 */
const PayoutModal = ({ order, onClose, onConfirm }) => {
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!order) return null;

  const orderAmount = parseFloat(order.final_price || order.price);
  const commissionPercent = 3.0;
  const commissionAmount = orderAmount * (commissionPercent / 100);
  const netAmount = orderAmount - commissionAmount;

  const handleConfirm = async () => {
    setProcessing(true);
    setError(null);

    try {
      const response = await onConfirm(order.id);
      setResult(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (!processing) {
      onClose();
    }
  };

  return (
    <div className="payout-modal-overlay" onClick={handleClose}>
      <div className="payout-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="payout-modal__header">
          <div className="payout-modal__icon">💰</div>
          <h2 className="payout-modal__title">
            {result ? 'Payout Created Successfully!' : 'Create Designer Payout'}
          </h2>
          <button
            className="payout-modal__close"
            onClick={handleClose}
            disabled={processing}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="payout-modal__content">
          {!result && !error && (
            <>
              {/* Order Details */}
              <div className="payout-modal__section">
                <h3 className="payout-modal__section-title">Order Information</h3>
                <div className="payout-modal__info-grid">
                  <div className="payout-modal__info-item">
                    <span className="payout-modal__info-label">Order ID:</span>
                    <span className="payout-modal__info-value">#{order.id}</span>
                  </div>
                  <div className="payout-modal__info-item">
                    <span className="payout-modal__info-label">Customer:</span>
                    <span className="payout-modal__info-value">{order.name}</span>
                  </div>
                  <div className="payout-modal__info-item">
                    <span className="payout-modal__info-label">Product:</span>
                    <span className="payout-modal__info-value">{order.productname}</span>
                  </div>
                  <div className="payout-modal__info-item">
                    <span className="payout-modal__info-label">Designer ID:</span>
                    <span className="payout-modal__info-value">#{order.designer_id}</span>
                  </div>
                </div>
              </div>

              {/* Commission Breakdown */}
              <div className="payout-modal__section">
                <h3 className="payout-modal__section-title">Commission Breakdown</h3>
                <div className="payout-modal__breakdown">
                  <div className="payout-modal__breakdown-row">
                    <span>Order Amount</span>
                    <span className="payout-modal__amount">₹{orderAmount.toFixed(2)}</span>
                  </div>
                  <div className="payout-modal__breakdown-row payout-modal__breakdown-row--deduction">
                    <span>Platform Commission (3%)</span>
                    <span className="payout-modal__amount payout-modal__amount--deduction">
                      - ₹{commissionAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="payout-modal__breakdown-divider"></div>
                  <div className="payout-modal__breakdown-row payout-modal__breakdown-row--total">
                    <span>Designer Receives (97%)</span>
                    <span className="payout-modal__amount payout-modal__amount--total">
                      ₹{netAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="payout-modal__warning">
                <div className="payout-modal__warning-icon">⚠️</div>
                <div className="payout-modal__warning-content">
                  <strong>Important:</strong> This will immediately transfer ₹{netAmount.toFixed(2)} to the designer's Razorpay account.
                  The money will be settled to their bank account within 1-2 business days.
                </div>
              </div>
            </>
          )}

          {/* Success Result */}
          {result && (
            <div className="payout-modal__success">
              <div className="payout-modal__success-icon">✅</div>
              <h3 className="payout-modal__success-title">Transfer Completed!</h3>
              <div className="payout-modal__success-details">
                <div className="payout-modal__success-item">
                  <span className="payout-modal__success-label">Transfer ID:</span>
                  <span className="payout-modal__success-value">{result.transfer_id}</span>
                </div>
                <div className="payout-modal__success-item">
                  <span className="payout-modal__success-label">Designer:</span>
                  <span className="payout-modal__success-value">{result.designer_name}</span>
                </div>
                <div className="payout-modal__success-item">
                  <span className="payout-modal__success-label">Amount Transferred:</span>
                  <span className="payout-modal__success-value payout-modal__success-value--amount">
                    ₹{result.net_amount.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="payout-modal__success-timeline">
                <div className="payout-modal__timeline-item payout-modal__timeline-item--complete">
                  <div className="payout-modal__timeline-dot"></div>
                  <div className="payout-modal__timeline-content">
                    <strong>Now:</strong> Money transferred to designer's Razorpay account
                  </div>
                </div>
                <div className="payout-modal__timeline-item">
                  <div className="payout-modal__timeline-dot"></div>
                  <div className="payout-modal__timeline-content">
                    <strong>1-2 Days:</strong> Money settled to designer's bank account
                  </div>
                </div>
                <div className="payout-modal__timeline-item">
                  <div className="payout-modal__timeline-dot"></div>
                  <div className="payout-modal__timeline-content">
                    <strong>Ready:</strong> Designer can withdraw funds
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="payout-modal__error">
              <div className="payout-modal__error-icon">❌</div>
              <h3 className="payout-modal__error-title">Payout Failed</h3>
              <p className="payout-modal__error-message">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="payout-modal__footer">
          {!result && !error && (
            <>
              <button
                className="payout-modal__button payout-modal__button--cancel"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className="payout-modal__button payout-modal__button--confirm"
                onClick={handleConfirm}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="payout-modal__spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>Create Payout - ₹{netAmount.toFixed(2)}</>
                )}
              </button>
            </>
          )}
          {(result || error) && (
            <button
              className="payout-modal__button payout-modal__button--close"
              onClick={handleClose}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayoutModal;
